import { toBinaryBase64 } from '~/utils/text.utils';
import { QuestionBlock } from '~/models/blocks/question-block.model';
import { RootStore } from '~/stores/root.store';
import { IFlowTheme, IVendorEmail } from '~/types/model.types';
import { convertSmartFieldsToServerFormat } from '~/common/SmartFields/smart-fields.utils';
import { trackError } from '~/services/logger/error.utils';
import { ICompanyResponse } from '~/types/server.types';
import { Coupons } from '~/constants/coupons';
import {
    deleteStoredCouponCode,
    getStoredCouponCode
} from '~/utils/coupons.utils';
import { isReactFirst } from '~/utils/environment.utils';
import { sanitizeSubjectContent } from '~/components/OneComposer/SubjectRow/Subject.utils';

export class CompanyController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    async fetchCompany(companyId: string, forceAngularReload = false) {
        const { injectorService } = this.rootStore; // for Angular's CompaniesManager, protected by isReactFirst
        const company = await this.rootStore.companyService.fetchCompany(
            companyId
        );
        this.rootStore.companyStore.update(company);
        this.rootStore.companyStore.loadFonts();

        if (!isReactFirst() && forceAngularReload) {
            injectorService.get('CompaniesManager').getCurrCompany(true);
        }

        return company;
    }

    async fetchCompanyFlows(companyId: string) {
        const { templatesService } = this.rootStore;

        const flows = await templatesService.fetchCompanyFlows({
            companyId,
            flow_type: 'template'
        });
        return flows;
    }

    async getWorkspaceCounts(selectedViewId?: string) {
        const { companyService } = this.rootStore;
        const countsByUser = await companyService.workspaceCounts(
            selectedViewId
        );
        return countsByUser;
    }

    async updateCompanyLeadSources(
        options,
        companyId: string,
        questionBlock?: QuestionBlock
    ) {
        const leadSources =
            await this.rootStore.companyService.updateCompanyLeadSources(
                options,
                companyId
            );

        if (questionBlock) {
            questionBlock.updateCompanyConnectedQuestion(leadSources);
            this.rootStore.blockCtrl.updateBlock(questionBlock, {
                question: questionBlock.question
            });
        }

        this.rootStore.companyStore.currCompany.updateLeadSources(leadSources);
    }

    async addCompanyServiceUnit(serviceUnit: string, companyId: string) {
        const { service_units } =
            await this.rootStore.companyService.addCompanyServiceUnit(
                serviceUnit,
                companyId
            );

        this.rootStore.companyStore.currCompany.updateServiceUnits(
            service_units
        );
    }

    async removeCompanyServiceUnit(serviceUnit: string, companyId: string) {
        const { service_units } =
            await this.rootStore.companyService.removeCompanyServiceUnit(
                serviceUnit,
                companyId
            );

        this.rootStore.companyStore.currCompany.updateServiceUnits(
            service_units
        );
    }

    async fetchEmailTemplate({ templateId }: { templateId: string }) {
        const {
            companyService,
            companyStore: { currCompany }
        } = this.rootStore;
        try {
            const { vendor_emails, requested_email } =
                await companyService.fetchEmailTemplates({
                    companyId: currCompany._id,
                    templateId
                });
            if (requested_email) {
                return requested_email;
            }
            const template = vendor_emails.find(
                email => email._id === templateId
            );
            if (!template) {
                trackError({
                    error: new Error(
                        `Template with id "${templateId}" not found`
                    ),
                    context: { vendor_emails }
                });
            }
            return template;
        } catch (error) {
            trackError({ error });
        }
    }

    getEmailTemplate(companyId: string, templateId: string) {
        return this.rootStore.companyService.getEmailTemplate(
            companyId,
            templateId
        );
    }

    loadFonts() {
        const { companyStore } = this.rootStore;
        companyStore.loadFonts();
    }

    async deleteFont(fontName) {
        const { companyService, companyStore } = this.rootStore;
        return companyService
            .deleteFont(fontName, companyStore.currCompany._id)
            .then(() => {
                companyStore.currCompany.markFontAsDeleted(fontName);
            });
    }

    /**
     * ? Why is it called load if it's updating the theme?
     */
    loadCompanyFlowTheme(companyFlowTheme: IFlowTheme) {
        const { companyStore } = this.rootStore;

        companyStore.currCompany.updateCompanyTheme(companyFlowTheme);
    }

    async updateCompanyTheme(companyId: string, theme: IFlowTheme) {
        const { companyService, companyStore } = this.rootStore;
        const companyFlowTheme = await companyService.updateCompanyFlowTheme(
            companyId,
            theme
        );
        companyStore.currCompany.updateCompanyTheme(companyFlowTheme);
        return companyFlowTheme;
    }

    async createEmptyEmail() {
        const {
            companyService,
            companyStore: { currCompany }
        } = this.rootStore;

        const { vendor_emails } = await companyService.createEmail(
            currCompany._id
        );

        currCompany.updateCompanyEmails(vendor_emails);

        return currCompany.vendor_emails[vendor_emails.length - 1];
    }

    async updateEmail(email: Partial<IVendorEmail>) {
        const {
            companyService,
            companyStore: { currCompany }
        } = this.rootStore;

        const serverFormatSubject = convertSmartFieldsToServerFormat(
            sanitizeSubjectContent({
                editorData: email.subject
            })
        );
        const serverFormatBody = convertSmartFieldsToServerFormat(
            email.html_body
        );
        const encodedBody = toBinaryBase64(serverFormatBody);

        const { vendor_emails } = await companyService.updateEmail(
            currCompany._id,
            {
                ...email,
                html_body: encodedBody,
                file_attachments: email?.file_attachments,
                image_attachments: email?.image_attachments,
                subject: serverFormatSubject,
                encoded: true
            }
        );

        currCompany.updateCompanyEmails(vendor_emails);

        return currCompany.vendor_emails.find(x => x._id === email._id);
    }

    async createEmail(email: Partial<IVendorEmail>) {
        const emptyNewEmail = await this.createEmptyEmail();
        const updatedEmail = await this.updateEmail({
            ...emptyNewEmail,
            ...email,
            _id: emptyNewEmail._id
        });
        return updatedEmail;
    }

    async deleteEmail(emailId: string, force?: boolean) {
        const {
            companyService,
            companyStore: { currCompany }
        } = this.rootStore;

        await companyService.deleteEmail(currCompany._id, emailId, force);
        // this endpoint returns the vendor emails with only _id and title
        // so instead of loading the response we're filtering the deleted email locally.
        currCompany.updateCompanyEmails(
            currCompany.vendor_emails.filter(x => x._id !== emailId)
        );
    }

    async cloneEmailTemplate(templateId: string) {
        const {
            companyService,
            companyStore: { currCompany }
        } = this.rootStore;

        const {
            file_attachments,
            html_body,
            image_attachments,
            subject,
            title
        } = currCompany.getEmailTemplate(templateId);

        const encodedBody = toBinaryBase64(html_body);

        var data = {
            encoded: !!encodedBody,
            file_attachments,
            html_body: encodedBody,
            image_attachments,
            save_as: true,
            subject: subject,
            title: title
        };

        const { vendor_emails } = await companyService.createEmail(
            currCompany._id,
            data
        );
        currCompany.updateCompanyEmails(vendor_emails);
        return vendor_emails[vendor_emails.length - 1];
    }
    async resetCompanySuggestedBrandElements() {
        const { companyService, companyStore } = this.rootStore;
        return companyService.resetCompanySuggestedBrandElements({
            companyId: companyStore.currCompany._id
        });
    }
    async setPicTimeAuthCode(clientCode: string, state: string) {
        const {
            companyService,
            companyStore: { currCompany }
        } = this.rootStore;

        if (clientCode) {
            await companyService.setPicTimeAuthCode(
                currCompany._id,
                clientCode,
                state
            );
        }
    }
    async disconnectPicTimeIntegration() {
        const {
            companyService,
            injectorService,
            userStore: { currUser },
            companyStore: { currCompany }
        } = this.rootStore;
        await companyService.disconnectPicTimePath(currCompany._id);

        if (!isReactFirst()) {
            const CompaniesManager = injectorService.get('CompaniesManager');
            await CompaniesManager.getCurrCompany(true);
        } else {
            await this.fetchCompany(currUser.company_id, false);
        }
        currCompany.setPicTimeIntegration(false);
        deleteStoredCouponCode();
    }

    async connectPrismmAccount(prismmAccountId: string) {
        const {
            companyStore: {
                currCompany: { _id: companyId }
            },
            companyService
        } = this.rootStore;

        try {
            await companyService.connectPrismmAccount(
                prismmAccountId,
                companyId
            );
        } catch (error) {
            trackError({
                error,
                message: 'Error connecting Prismm account to company',
                context: {
                    prismm_account_id: prismmAccountId,
                    companyId
                }
            });
            throw error;
        }

        this.updateCompanyPrismmIntegrationState(true);
    }

    async disconnectPrismmAccount() {
        const {
            companyStore: {
                currCompany: { _id: companyId }
            },
            companyService
        } = this.rootStore;

        try {
            await companyService.disconnectPrismmAccount(companyId);
        } catch (error) {
            trackError({
                error,
                message: 'Error disconnecting Prismm account from company',
                context: { company_id: companyId }
            });
            throw error;
        }

        // We want to remove the previously auto-applied Prismm coupon if user disconnects Prismm account
        const storedCouponCode = getStoredCouponCode();
        if (storedCouponCode === Coupons.prismm) {
            deleteStoredCouponCode();
        }

        this.updateCompanyPrismmIntegrationState(false);
    }

    private updateCompanyPrismmIntegrationState(isIntegrated: boolean) {
        const {
            companyStore: { currCompany }
        } = this.rootStore;
        currCompany.setPrismmIntegration(isIntegrated);
        this.updateNgCompany({
            has_prismm_integration: isIntegrated
        });
    }

    updateNgCompany(data: Partial<ICompanyResponse>) {
        if (isReactFirst()) {
            return;
        }
        // Hopefully temporary until this is merged https://github.com/HoneyBook/honeybook-react/pull/5809
        const { injectorService } = this.rootStore;
        const currCompany = injectorService
            ?.get('CompaniesManager')
            ?.getCurrCompany();
        if (currCompany) {
            Object.assign(currCompany, data);
        }
    }

    async fetchCompanies() {
        const {
            companyService,
            userStore: { currUser }
        } = this.rootStore;
        const companies = await companyService.fetchCompanies(
            currUser.account._id
        );

        this.rootStore.userStore.currUser.setCompanies(companies);

        return companies;
    }
}
